import { HttpErrorResponse, HttpInterceptorFn, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, catchError, filter, finalize, Observable, switchMap, take, throwError } from 'rxjs';
import { AuthSessionService } from '../../core/services/auth-session.service';
import { AuthenticationService } from '../../core/services/authentication.service';

const PUBLIC_PATHS = [
  '/token',
  '/token/refresh-token',
  '/user/check/pre-authorization',
  '/registration',
  '/registration/confirm',
  '/registration/verify',
  '/registration/signup',
  '/participant',
  '/participant/confirm',
  '/password',
  '/password/verify',
  '/password/set',
  '/open',
];

let isRefreshing = false;
const accessTokenSubject = new BehaviorSubject<string | null>(null);

function isPublicRequest(url: string): boolean {
  return PUBLIC_PATHS.some((path) => url.includes(path));
}

function isAssetRequest(url: string): boolean {
  return url.startsWith('/assets/') || url.includes('/assets/');
}

function addTokenHeader(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
      ...(request.headers.has('Content-Type') ? {} : { 'Content-Type': 'application/json' }),
    },
  });
}

function handleUnauthorizedError(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authSession: AuthSessionService,
  authentication: AuthenticationService
): Observable<any> {
  if (isRefreshing) {
    return accessTokenSubject.pipe(
      filter((token): token is string => !!token),
      take(1),
      switchMap((token) => next(addTokenHeader(request, token)))
    );
  }

  isRefreshing = true;
  accessTokenSubject.next(null);

  const refreshToken = authSession.getRefreshToken();
  if (!refreshToken) {
    isRefreshing = false;
    authentication.logout();
    return throwError(() => new Error('No refresh token'));
  }

  return authentication.refreshToken(refreshToken).pipe(
    switchMap((response) => {
      const newAccessToken = response.accessToken;
      const newRefreshToken = response.refreshToken;

      authSession.updateTokens(newAccessToken, newRefreshToken);
      accessTokenSubject.next(newAccessToken);

      return next(addTokenHeader(request, newAccessToken));
    }),
    catchError((error) => {
      accessTokenSubject.next(null);
      authentication.logout();
      return throwError(() => error);
    }),
    finalize(() => {
      isRefreshing = false;
    })
  );
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (isAssetRequest(req.url)) {
    return next(req);
  }

  const authSession = inject(AuthSessionService);
  const authentication = inject(AuthenticationService);

  if (isPublicRequest(req.url) || req.headers.has('Authorization')) {
    return next(req);
  }

  const token = authSession.getToken();
  if (!token) {
    return next(req);
  }

  const authReq = addTokenHeader(req, token);

  return next(authReq).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        return handleUnauthorizedError(req, next, authSession, authentication);
      }

      return throwError(() => error);
    })
  );
};
